/** @module csi/heading/Attribute */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"csi/heading/_base",
	"csi/heading/AttributeAction",
	"csi/heading/AttributeComment",
	"csi/heading/AttributeCommentReset",
	"csi/heading/DisableExpression",
	"csi/heading/Renderer",
	"csi/heading/RestoreExpression",
	"csi/html",
	"csi/locale",
	"csi/sessionConstants",
	"csi/utility/htmlUtility",
	"csi/widget/Tooltip",
	"csi/xml",
	"csi/xml/Writer"
], function(declare, lang, domClass, csiHeading, AttributeAction, AttributeComment, AttributeCommentReset, DisableExpression, Renderer, RestoreExpression, html, locale, sessionConstants, {getTrimmedInnerText, purifyForHtml}, Tooltip, xml, Writer) {

	return declare(Renderer, {
		// Boolean: set to true/false to force allowing/restricting copy-paste operations for this heading
		//bCopy: null,

		// the default value to return for container, if entry for the key does not exist
		//defaultContainerValue: null,

		/** @property {boolean} editableOnFrozenNode - if true, then the attribute is editable even though the node is frozen   */
		editableOnFrozenNode: false,

		/** @property {boolean} onlyCopyToEmpty - only allow copying a non-empty value to nodes with an empty value */
		onlyCopyToEmpty: false,

		// String[]: array of query operators available for filtering this type of attribute (not including blanks)
		//queryOps: null,

		/** @property {boolean} restrictQuery - if true, then the filter/group/sort will be disabled */
		//restrictQuery: false,

		canComment: function(/*Context*/ctx, /*Heading*/h) {
			// summary: allow commenting if not in form and edits not restricted and not explicitly disable commenting; 
			// check for Heading/@Id to handle case where
			// renderer may be derived from for special non-attribute cases
			return !ctx.bReadOnlyView && !ctx.defView.bRestrictComments && !!h.m_id && (h.getRight() != xml.RIGHT_NONE);
		},

		/**
		 * Return if drag-fill allowed from copy node
		 * @param {Context} ctxCopy 
		 * @param {Heading} h 
		 * @return {boolean}
		 */
		canCopy(ctxCopy, h) {
			if (this.restrictsCopy(h)) {
				return false;
			}

			if (!this.bCopy && !h.bCopy) {
				// do not copy attributes with renderer classes;
				// do not allow if heading has sub-headings;
				// restrict any actions that have require confirmation or special javascript
				if (h.classRenderer || h.m_subheadings || h.m_action?.hasSpecialLogic()) {
					return false;
				}
			}

			// if specified, only allowing copying non-empty value (to nodes with empty value)
			if (this.onlyCopyToEmpty && !this._attributeHasValidValue(ctxCopy, h)) {
				return false;
			}

			return true;
		},

		/**
		 * @override
		 * Do not allow edit by drag and drop if attribute can only be copied when empty (value can be filled, but not reset)
		 */
		canEditByDragAndDrop(ctx, h) {
			return this.inherited(arguments) && (!this.onlyCopyToEmpty || !this._attributeHasValidValue(ctx, h));
		},

		/**
		 * Return if drag-fill allowed from copy node to paste node
		 * @param {Context} ctxCopy 
		 * @param {Context} ctxPaste 
		 * @param {Heading} h 
		 * @return {boolean}
		 */
		canPaste(ctxCopy, ctxPaste, h) {
			// if specified, only allowing copying non-empty value (to nodes with empty value)
			if (this.onlyCopyToEmpty && this._attributeHasValidValue(ctxPaste, h)) {
				return false;
			}

			return true;
		},

		/**
		 * @override
		 * Return true if cell is inline editable
		 */
		canPasteToCell(ctx, refNode) {
			return csiHeading.isHeadingNodeTabStop(refNode);
		},

		/**
		 * Generic code to return query spec for all attribute types
		 */
		getQuerySpec: function(ctx, h, spec) {
			// FIX: for now support both extended and non-extended paths, but based on all testing,
			// extended path always exists; in future it will be mandatory
			var ep = spec.getExtendedPath();
			var path = h.m_path;
			var id = h.m_id;
			var index = (spec.index == null) ? h.m_index : spec.index;
			var prop;
			const ops = this.onSetQuerySpecOperator(csiHeading.allowTextComparisonOperators, this.queryOps, spec);

			// if heading is for Node Type attribute, then filter by node property rather than attribute
			if (id == xml.ATTR_TYPE) {
				id = null;
				prop = xml.PROP_TYPE;
			}

			if (ep) {
				// use extended path syntax in query path
				path = ep.getQueryPath();
			} else if (h.isMatrixSubHeading()) {
				// if heading is in matrix, then prepend path to matrix 
				path = xml.path(h.m_parent.matrixGroupPath, path);
			}

			return {
				id: id,
				index: index,
				path: path,
				prop: prop,
				ops,
				relative: true,
				ren: this,
				type: this.type
			};
		},

		/**
		 * modify the operators based on restoreTextComparisonOperators
		 * @param {boolean} restoreTextComparisonOperators 
		 * @param {string[]} operators 
		 * @param {Spec} spec
		 * @returns string[]
		 */
		onSetQuerySpecOperator(restoreTextComparisonOperators, operators, spec) {
			return operators;
		},

		/**
		 * @override
		 * If editableOnFrozenNode==true, allow editing of attribute even when the node is Frozen
		 */
		reserveContext(ctx, h) {
			if (this.editableOnFrozenNode) {
				ctx.pushParams();
				ctx.setReadOnly(false);
			}
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Pop context applied above
		 */
		releaseContext(ctx, h) {
			if (this.editableOnFrozenNode) {
				ctx.pop();
			}
			this.inherited(arguments);
		},

		/**
		 * Return true if renderer or Heading explicitly restricts copying
		 * @param {Heading} h
		 * @return {boolean}
		 */
		restrictsCopy(h) {
			return (this.bCopy === false) || (h.bCopy === false);
		},

		/**
		 *  add tooltip for the node attribute
		 */
		toTooltip: function(/*Context*/ctx, /*Heading*/h, /*csi.view.Tooltip*/tooltip) {
			// summary: add content and actions to tooltip

			// add heading tooltip
			if (ctx.view.useMuiTooltipStyling) {
				const customFieldValue = this.headingToText(ctx, h);
				const customFieldTooltip = h?.m_tooltip ?? "";
				if (customFieldValue || customFieldTooltip) {
					tooltip.addContent(Tooltip.formatCustomField(customFieldValue, customFieldTooltip));
				}
			} else if (h?.m_tooltip) {
				tooltip.addContent(html.create("div", {
					multiLineText: h.m_tooltip
				}));
			}

			// if action to edit attribute is designated to the tooltip, then add it first
			var actionData = this._getRenderAction(ctx, h);
			if (actionData?.bTooltip) {
				tooltip.addAction(actionData.action);
			}

			if (!ctx.hasContainerKey() || this.bSupportContainerKeyTooltip) {
				// add comment if any
				var cmt = ctx.canRenderComments(true) ? this._getComment(ctx, h) : null;
				if (cmt) {
					tooltip.addLabeledContent(locale.resource("Comment"), html.CSS_RICH_TEXT).innerHTML = purifyForHtml(cmt.replace(/\\n/g, "<br>"));
				}

				// add action for handling editable attributes with expressions
				var action = this._getExpressionAction(ctx, h, actionData, tooltip);
				if (action) {
					tooltip.addAction(action);
				}

				// if in popup dialog or heading explicitly set to not be editable, then do not allow commenting
				if (this.canComment(ctx, h)) {
					// return comment action
					var add = new AttributeComment();
					add.heading = h;
					add.resolve();

					var reset = new AttributeCommentReset();
					reset.heading = h;
					reset.resolve();

					tooltip.addAction(add);
					tooltip.addAction(reset);
				}
			}
		},

		toTooltipEdit: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			// summary: return DOMNode to display as tooltip while editing
			var idRes;

			if (domClass.contains(refNode, csiHeading.CSS_EDITABLE_EXP)) {
				// expression is enabled, so warn user about overwriting it
				idRes = "ActionAttribute_Exp";
			} else if (domClass.contains(refNode, csiHeading.CSS_EDITABLE_OVER)) {
				// expression is disabled
				idRes = "ActionAttribute_ExpOver";
			} else {
				return null;
			}

			return html.create("div", {
				innerText: locale.resource(idRes)
			});
		},

		/**
		 * Return true if value of attribute in context is valid
		 * @param {Context} ctx 
		 * @param {Heading} h 
		 * @return {boolean}
		 */
		_attributeHasValidValue(ctx, h) {
			const type = h.getAttrType();
			const value = this._getValue(ctx, h);
			return xml.valueIsValid(value, type);
		},

		_toHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData, /*Boolean*/bSub) {
			// summary: add attribute tooltip if present
			var ret = this.inherited(arguments);
			var cmt = ctx.canRenderComments() ? this._getComment(ctx, h) : null;
			if (cmt) {
				csiHeading.toComment(ctx, domClass.contains(refNode, csiHeading.CSS_PRIMARY) ? refNode.parentNode : refNode, cmt);
			}

			return ret;
		},

		_getAction: function() {
			// summary: return default action for attributes
			return lang.mixin(new AttributeAction(), {
				editableOnFrozenNode: this.editableOnFrozenNode
			});
		},

		/**
		 * Return parameters to use for aggregate store request
		 * @param {Context} ctx 
		 * @param {Heading} h 
		 * @return {IAggregateStoreParams}
		 */
		_getAggregateStoreParams(ctx, h) {
			const {keyValue, locale, view: {writerForActions}} = ctx;
			const params = {
				attributeId: h.m_id,
				locale,
				nodeType: ctx.getNodeType(),
				sourceUrl: ctx.getNodeURL(),
			};

			if (ctx.hasContainerKey()) {
				params.attributeContainerIndex = keyValue;
			}

			if (writerForActions?.isChanged()) {
				params.reportOperations = writerForActions.serialize();
			}

			return params;
		},

		/**
		 * Return text for attribute comment
		 * @param {Context} ctx 
		 * @param {Heading} h 
		 * @return {string | undefined}
		 */
		_getComment(ctx, h) {
			const cmt = xml.nodeGetAttributeValue(ctx.getNode(), xml.idGetComment(h.m_id));
			if (cmt && getTrimmedInnerText(cmt)) {
				return cmt;
			}
		},

		_getRenderAction: function(/*Context*/ctx, /*Heading*/h) {
			// summary: refNode will be styled differently if it is editable with an expression
			var node = ctx.getNode();
			var attrId = h.m_id;

			// If attribute has one time expression and does not have node type (evaluate once - not yet evaluated)
			// then do not allow editing (return no action aka null)
			// xml.nodeAttributeGetType() returns null if there are no such attribute on the node or if node is not present
			// if attribute exists but it's type is not set (our case) - it returns "", so we compare it against empty string
			if (h._rca?.isOneTimeExp && xml.nodeAttributeHasExpression(node, attrId) && xml.nodeAttributeGetType(node, attrId) == "") {
				return null;
			}

			var ad = this.inherited(arguments);
			if (ad && !ad.cssEdit) {
				var fg = xml.nodeAttributeGetFlags(node, attrId);
				if (fg) {
					if (xml.valueHasFlag(fg, xml.ATTR_FLAG_DISABLE_EXPRESSION)) {
						// if expression is disabled, then it has been overridden by manual value
						ad.cssEdit = csiHeading.CSS_EDITABLE_OVER;
					} else if (xml.valueHasFlag(fg, xml.ATTR_FLAG_HAS_EXPRESSION) &&
						!xml.valueHasFlag(fg, xml.ATTR_FLAG_ONE_TIME_EXPRESSION) &&
						!xml.valueHasFlag(fg, xml.ATTR_FLAG_ONE_TIME_ROLLUP)) {
						// attribute is run by expression but can be overridden
						ad.cssEdit = csiHeading.CSS_EDITABLE_EXP;
					}
				}
			}
			if (ad?.cssEdit) {
				ad.cssEdit += csiHeading.getLargeEditableCSS();
			}

			return ad;
		},

		_getRenderCSS: function(/*Context*/ctx, /*Heading*/h) {
			// summary: check for attributes storing special styling
			var node = ctx.getNode();
			if (!node || !ctx.canRenderComments()) {
				return this.css;
			}

			var commentColor = node[xml.idGetCommentColor(h.m_id)];

			return this.css +
				((commentColor && !ctx.bCard) ? " " + commentColor : "") +
				(xml.nodeAttributeHasFlag(node, h.m_id, xml.ATTR_FLAG_OUT_OF_SYNC) ? " csi-bg-yellow" : "");
		},

		_getExpressionAction: function(/*Context*/ctx, /*Heading*/h, /*ActionData*/actionData, /*csi.view.Tooltip?*/tooltip) {
			// summary: return restore expression action if appropriate
			if (!actionData) {
				return null;
			}

			var action;

			if (actionData.cssEdit?.includes(csiHeading.CSS_EDITABLE_EXP)) {
				action = new DisableExpression();
			} else if (actionData.cssEdit?.includes(csiHeading.CSS_EDITABLE_OVER)) {
				action = new RestoreExpression();

				if (tooltip) {
					// invoke writer to go calculate the expression - set as report so change is not persisted
					var url = ctx.getNodeURL();
					var writer = new Writer();
					writer.setReport();
					writer.addParameter("ReturnNodes", "Changed");
					writer.changeNode(url).restoreAttributeExp(h.m_id);

					// add calculated value to tooltip - change cache for context to include response from writer
					var res = writer.execute({sync: true});

					csiHeading.addRestoreExpressionToTooltip(tooltip, ctx, h, this, res.getNode(url));
				}
			}

			if (action) {
				action.heading = h;
				action.resolve();
			}

			return action;
		},

		_getValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: extract attribute value - if within container, then extract single value
			var node = ctx.getNode();
			if (!node) return null;

			var v = node[h.m_id];

			if (v && ctx.hasContainerKey()) {
				v = v[ctx.keyValue];

				if (v == null) { // the entry for the key does not exist
					v = this.defaultContainerValue;
				}
			}

			return v;
		}
	});
});
